import { createContext, useContext, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { getSystemConfigurations } from '@/resources/support-tools-service.resource';
import { config as configBase } from '../config';
import {
  getChainInfo,
  getNftCollections,
  getBlockchainConfigurations,
} from '@/resources/nft-service.resource';

const initErrorState = {
  error: false,
  message: '',
};

const configs = configBase;

export const SystemContext = createContext();

export const useSystemContext = () => useContext(SystemContext);

export function SystemProvider({ children }) {
  const { fetcher, user, userHasPermissions } = useAuth();
  const [config, setConfig] = useState({});

  const [systemConfigError, setSystemConfigError] = useState(initErrorState);

  const handleFetchSystemConfigurations = () => {
    return fetcher(getSystemConfigurations())
      .then((data) => {
        return data;
      })
      .catch((err) => {
        let message = '';
        if (err?.error?.status === 401 || err?.error?.status === 403) {
          message = 'You do not have permission to view Settings.';
        } else {
          message =
            'An error occurred while fetching Settings data. Please try again later.';
        }
        setSystemConfigError({
          error: true,
          message: message,
        });
        return [];
      });
  };

  const { data: systemConfigurationsData = [], isLoading: systemConfigIsLoading } =
    useQuery(['systemConfigurations'], handleFetchSystemConfigurations, {
      enabled:
        !!user.accessToken &&
        userHasPermissions('supporttools.systemconfigurations.view'),
      retry: false,
    });

  const { data: nftCollections } = useQuery(
    ['nftCollections'],
    () => fetcher(getNftCollections()),
    {
      enabled: !!user?.uid,
      retry: false,
    }
  );

  const { data: chainInfo } = useQuery(['chainInfo'], () => fetcher(getChainInfo()), {
    enabled: !!user?.uid,
    retry: false,
  });

  const { data: blockchainConfigurations } = useQuery(
    ['blockchainConfigurations'],
    () => fetcher(getBlockchainConfigurations()),
    {
      enabled: !!user?.uid,
      retry: false,
      select: (data) => data.blockchainConfigurations,
    }
  );

  const setConfigs = () => {
    const conf = {};
    systemConfigurationsData.forEach((config) => {
      const newKey = config.key[0].toLowerCase() + config.key.slice(1);
      conf[newKey] = config.value;
    });

    Object.keys(conf).forEach((key) => {
      configs.brand[key] = conf[key] || configs.brand[key];
    });

    setConfig(configs);
  };

  useEffect(() => {
    setConfigs();
  }, [systemConfigurationsData]);

  return (
    <SystemContext.Provider
      value={{
        blockchainConfigurations,
        chainInfo,
        handleFetchSystemConfigurations,
        nftCollections,
        systemConfigError,
        systemConfigurationsData,
        setSystemConfigError,
        systemConfigIsLoading,
        config,
      }}
    >
      {children}
    </SystemContext.Provider>
  );
}
